import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";

/**
 * Minimal in-app offline banner. The app shell (index.html) is now cached and
 * served via navigateFallback, so a truly offline visit loads the SPA and data
 * calls fail. This banner surfaces that state using the same seal colors as
 * public/offline.html.
 */
export function OfflineBanner() {
  const [online, setOnline] = useState<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: "1rem",
        zIndex: 9999,
        background: "#1F3D2B",
        color: "#F7F5F0",
        border: "1px solid rgba(228,184,74,0.4)",
        borderRadius: 6,
        padding: "0.75rem 1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        boxShadow: "0 4px 24px -8px rgba(0,0,0,0.35)",
        fontFamily: "'Lato', -apple-system, sans-serif",
        maxWidth: "calc(100vw - 2rem)",
      }}
    >
      <WifiOff size={18} color="#E4B84A" />
      <div style={{ fontSize: "0.9rem" }}>
        <strong style={{ color: "#E4B84A" }}>You're offline.</strong>{" "}
        Some records won't load until the connection returns.
      </div>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: "#E4B84A",
          color: "#1F3D2B",
          border: "none",
          padding: "0.4rem 0.75rem",
          borderRadius: 4,
          fontWeight: 600,
          cursor: "pointer",
          minHeight: 36,
        }}
      >
        Retry
      </button>
    </div>
  );
}
